import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { UiPath } from '../components/UiPath';

type Row = { k: string; v: ReactNode };

const TABS: Row[] = [
  {
    k: 'General',
    v: 'Reporting currency, invoice defaults, churn grace period, automatic emails, renewal invoice timing, working days and what the Assistant remembers. Steps 02 to 04 and 09.',
  },
  {
    k: 'Business',
    v: 'A short description of your company that the OKR review and the Revvly Assistant read as background, plus Focus this period and Constraints, which only a person fills in. It can pull a draft of the rest from your website.',
  },
  {
    k: 'Invoice',
    v: 'The Invoice Header and Payment Information that print on every invoice PDF. Step 05.',
  },
  {
    k: 'Tax',
    v: 'Tax registrations, the rates you publish, and reference data. Shown only with the Tax add-on. Everyone can read it; admins edit.',
  },
  {
    k: 'Meters',
    v: 'The metered units your components bill against, such as seats, GB or transactions. Defined once and reused.',
  },
  {
    k: 'Catalog',
    v: 'What the invoice import has learned about your products. Read only. Step 07.',
  },
  {
    k: 'Teams',
    v: 'Named groups of people with a lead. What a team owns is decided by the feature that uses it.',
  },
  {
    k: 'Roles',
    v: 'What someone may do inside a feature, granted separately from billing and account administration.',
  },
  {
    k: 'Seats',
    v: 'How many people can sign in against your limit, and a form to ask for more. The limit and price are changed by Revvly.',
  },
  {
    k: 'Modules',
    v: 'Turns OKRs on or off for the workspace. The badge reads On, Off or Not in your plan.',
  },
  {
    k: 'Notifications',
    v: 'The workspace default for the check-in rhythm, and what was delivered in the last seven days. Each person can set their own on Account.',
  },
  {
    k: 'Delivery log',
    v: 'Every message the product tried to send, including ones it chose not to, filtered by outcome.',
  },
  {
    k: 'Import',
    v: 'The Import Data hub for CSV and Excel files. It is also reachable at its own address.',
  },
  {
    k: 'AI spend',
    v: 'What the email intelligence tiers have spent against their monthly ceiling. Read only. Step 06.',
  },
  {
    k: 'API Integration',
    v: 'External API Configuration, which connects the workspace to an external API for incubator management.',
  },
  {
    k: 'API Keys',
    v: 'Issue, rotate and revoke the keys your integrations use, and set each key’s rate limits. Step 08.',
  },
  {
    k: 'Webhooks',
    v: 'Web addresses Revvly posts to when a lead is created, changed or deleted, for example into Zapier or a sheet.',
  },
  { k: 'Integrations', v: 'Cards for Slack, ClickUp and Google Drive. Step 08.' },
  {
    k: 'Booking intake',
    v: 'The Calendly connection, the scheduling links that create companies, a Daily limit, and Undo a run.',
  },
  {
    k: 'Situations',
    v: 'The numbers behind the nightly account situations: what counts as quiet, waiting or going dark. Changes apply at the next nightly sweep.',
  },
  {
    k: 'Templates',
    v: 'Email templates and Payment reminders. Shown when the workspace has CRM or Finance.',
  },
  {
    k: 'Check-ins',
    v: (
      <>
        Daily check-ins asked in a Slack DM, on your working days. Shown only with the Check-ins
        feature, and needs Slack connected. See{' '}
        <Link to="/docs/guides/daily-checkins">Run daily check-ins</Link>.
      </>
    ),
  },
  {
    k: 'Support',
    v: 'Support settings: General, Ticket types & workflows, Client updates, Secret keys, Webhooks and Import. Shown only with Support. Support roles are set on the Knowledge page, under Roles.',
  },
];

const GENERAL: Row[] = [
  {
    k: 'Reporting Currency',
    v: 'The currency every ARR and MRR figure is converted into for analytics. Default USD.',
  },
  {
    k: 'Default Payment Terms',
    v: 'Number of days after invoice date, from 1 to 365, or Due on receipt. Default 30 days. A client or an invoice can override it.',
  },
  {
    k: 'Churn Grace Period',
    v: 'If a customer starts a new subscription within this many days (1 to 365) of the last one ending, it counts as a renewal, not a churn. Default 30 days.',
  },
  {
    k: 'Automatically send emails',
    v: 'On by default. When off, scheduled emails such as payment reminders are paused and you send them yourself.',
  },
  {
    k: 'Renewal Invoice Timing',
    v: 'Days before renewal to generate the invoice, set separately for monthly, quarterly and yearly subscriptions (0 to 365). 0 invoices on the renewal date, and is the default. A client can override each period. Only the recurring base charge is billed ahead; usage is billed at renewal.',
  },
];

const WORKING: Row[] = [
  {
    k: 'Days',
    v: 'Seven checkboxes, Monday to Sunday. Monday to Friday until you change them. At least one must stay ticked.',
  },
  {
    k: 'Timezone',
    v: 'Decides which calendar day it is when a due date is worked out, and is the fallback timezone for scheduled messages. UTC when none is set.',
  },
  {
    k: 'Who and when',
    v: 'Tenant admins only. A change applies to follow-ups created afterwards; existing due dates do not move.',
  },
];

const MEMORY: Row[] = [
  {
    k: 'For everyone',
    v: 'Memories every member’s Assistant reads. Only tenant admins add, change or remove them.',
  },
  {
    k: 'Just for you',
    v: 'Memories only your own Assistant reads. No one else sees them, admins included.',
  },
  {
    k: 'Change and remove',
    v: 'The pencil rewords a memory in place; the bin removes it. Each memory is one sentence of up to 300 characters.',
  },
  {
    k: 'Show what the agent sees',
    v: 'Prints the exact text the Assistant is given at the start of every turn. It is information from your team, not instructions.',
  },
  {
    k: 'Review with the agent',
    v: 'Opens the Assistant with a question about what it remembers already typed. You send it; you can then ask it to reword or forget anything.',
  },
];

export function WorkspaceSettingsGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Configure your workspace — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Workspace settings</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Configure your <em>workspace</em>.
        </h1>
        <p className="dp-oneliner">
          Org Settings holds the defaults the rest of Revvly reads: your reporting currency, payment
          terms, working days, invoice header and connections. This page names every tab, then
          covers the ones that need a decision.
        </p>
      </header>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">01</span>
          Every Settings tab
        </h2>
        <UiPath
          steps={['Org Settings']}
          note="It opens on General. Tabs that depend on a feature are absent when the workspace does not have it."
        />
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Users is not a Settings tab. It has its own sidebar item; see{' '}
          <Link to="/docs/guides/add-user">Add a user</Link>.
        </p>
        <Table rows={TABS} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">02</span>
          General: currency and invoice defaults
        </h2>
        <p className="dp-plain">
          Change the fields, then choose Save Settings. The button stays disabled until something
          changes.
        </p>
        <Table rows={GENERAL} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">03</span>
          Working days and business-day arithmetic
        </h2>
        <p className="dp-plain">
          The Working days card sits under the General settings. Revvly counts business days against
          it, rather than assuming Monday to Friday. Today it drives the follow-up task created when
          you send an email (counting starts tomorrow, and the result is always a working day) and
          the days a Slack check-in runs when the check-in follows the working days. Invoices,
          renewals and payment reminders always use calendar days.
        </p>
        <UiPath steps={['Org Settings', 'General', 'Working days', 'Save working days']} />
        <Table rows={WORKING} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">04</span>
          Billing policy defaults
        </h2>
        <p className="dp-plain">
          Renewal invoice timing is the billing policy you set on the General tab. A few more
          workspace defaults exist and have no field on a settings tab today, so treat them as
          platform defaults.
        </p>
        <ul className="dp-connects" style={{ marginTop: 18 }}>
          <li>
            Reactivation window: 90 days after a subscription ends, beyond which a returning
            customer is a reactivation, not a renewal.
          </li>
          <li>
            Default commitment for prepaid-term schedules: Binding, so the customer owes the span
            they prepaid.
          </li>
          <li>
            Default activation gap for deferred-activation schedules: 60 days before the daily sweep
            raises the unactivated subscription.
          </li>
        </ul>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          A client page can override payment terms and renewal timing for that client alone. See{' '}
          <Link to="/docs/guides/billing-timing">Billing timing</Link> and{' '}
          <Link to="/docs/guides/billing-schedules">Billing schedules</Link>.
        </p>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">05</span>
          Invoice header and payment information
        </h2>
        <p className="dp-plain">
          The Invoice tab has two cards. Invoice Header holds the issuer details and logo printed at
          the top of every invoice PDF. Payment Information holds banking details and payment
          instructions printed on every invoice. Saving either, and uploading the logo, is limited
          to tenant admins. The step by step is in{' '}
          <Link to="/docs/guides/customize-invoices">Customize invoices</Link>.
        </p>
        <UiPath steps={['Org Settings', 'Invoice']} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">06</span>
          AI spend
        </h2>
        <p className="dp-plain">
          The AI spend tab is a readout, not a control. It has one panel for Tier 1 email triage and
          one for Tier 2 commitment extraction. Each shows a month by month table with Committed,
          Settled, Calls, Avg / call and Remaining against that tier’s monthly ceiling. The ceiling
          comes from platform configuration, so the page has no field to raise it, and a tier makes
          no calls unless a model, a key and a positive monthly ceiling are all configured.
        </p>
        <div className="api-note" style={{ marginTop: 18 }}>
          <span className="api-note-icon" aria-hidden="true">
            i
          </span>
          <div>
            Support has its own spend control. On Settings, Support, General, Daily AI spend cap
            takes a limit from 0 to 100,000 USD per day. There is no cap until you set one.
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">07</span>
          Catalog learning
        </h2>
        <p className="dp-plain">
          The Catalog tab reports how far the invoice import trusts each product. It has no autonomy
          switch to flip. A product is matched without review once 3 confirmations from 2 different
          clients agree with it, and each product row shows its status (Earned, Learning or Needs
          attention), Confirmed, Clients, Corrected, You supplied and What is left. A product that
          is corrected after earning drops to Needs attention.
        </p>
        <ul className="dp-connects" style={{ marginTop: 18 }}>
          <li>
            Matched without review counts invoices linked unseen and invoices still waiting, with
            Spot-check the sample to confirm a few of the unseen ones.
          </li>
          <li>
            Missing from the catalog lists billed clients whose invoices the catalog does not
            explain, with Create in catalog per proposed product.
          </li>
        </ul>
        <UiPath steps={['Org Settings', 'Catalog']} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">08</span>
          Keys and connections
        </h2>
        <Table
          rows={[
            {
              k: 'API Keys',
              v: 'Admins issue, rotate and revoke keys, choose an access level (Read, or Read & write, with optional extras) and set per-second and per-minute limits. See API keys for integrators and Limits and defaults.',
            },
            {
              k: 'Slack',
              v: 'Connect Slack, Re-check or Disconnect on the Slack card. Connecting is an admin action. It drives check-in prompts and announcements.',
            },
            {
              k: 'ClickUp',
              v: 'Connect ClickUp to link its tasks to key results. Connecting is an admin action.',
            },
            {
              k: 'Google Drive',
              v: (
                <>
                  Connect Google Drive to sync PDF invoices from shared folders. With CRM on,
                  personal Gmail is connected from Account instead. Once Drive is connected,
                  workspaces with client folders see a Client folders card under it, which keeps a
                  Drive folder for each client and prospect. See{' '}
                  <Link to="/docs/guides/drive-folders">Client folders in Google Drive</Link>.
                </>
              ),
            },
            {
              k: 'Calendly',
              v: 'Connected on the Booking intake tab, not Integrations. Bookings from known clients are recorded as meetings; links listed there also create companies, up to the Daily limit (100 a day until an admin changes it).',
            },
          ]}
        />
        <ul className="dp-connects" style={{ marginTop: 18 }}>
          <li>
            <Link to="/docs/guides/api-keys" style={{ color: 'inherit', textDecoration: 'none' }}>
              API keys for integrators ›
            </Link>
          </li>
          <li>
            <Link to="/docs/reference/limits" style={{ color: 'inherit', textDecoration: 'none' }}>
              Limits and defaults ›
            </Link>
          </li>
        </ul>
      </section>
      <section className="dp-section">
        <h2>
          <span className="dp-step-num">09</span>
          Agent memory
        </h2>
        <p className="dp-plain">
          The Agent memory card lists what the Assistant keeps in mind in every conversation:
          memories for everyone in the workspace, and your own. Most are saved from a conversation
          when you approve a Remember card; you can also add one here.
        </p>
        <UiPath steps={['Org Settings', 'General', 'Agent memory']} />
        <Table rows={MEMORY} />
        <p className="dp-plain" style={{ marginTop: 18 }}>
          More on how the Assistant uses them is in{' '}
          <Link to="/docs/assistant">the Assistant guide</Link>.
        </p>
      </section>
    </div>
  );
}

function Table({ rows }: { rows: Row[] }) {
  return (
    <div className="dp-fields-table" style={{ marginTop: 18 }}>
      {rows.map((r) => (
        <div className="dp-field-row" key={r.k}>
          <div className="dp-field-k">{r.k}</div>
          <div className="dp-field-v">{r.v}</div>
        </div>
      ))}
    </div>
  );
}
